import { onBeforeUnmount, watch } from 'vue';
import { useAuthStore } from '@/stores/auth';

/** Default inactivity window before an authenticated user is signed out. */
export const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
/** How often the idle check runs. Detection is accurate to within one tick. */
export const IDLE_CHECK_INTERVAL_MS = 15 * 1000;
/** Per-tab timestamp of the last activity, so a reload can't grant a fresh window. */
export const IDLE_STORAGE_KEY = 'lastActivityAt';

/** DOM events that count as "the user is still here". */
const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'wheel',
  'touchstart',
  'scroll',
  'click',
] as const;

export interface IdleLogoutOptions {
  /** Called when the inactivity window elapses (redirect + notify live here). */
  onTimeout: () => void;
  timeoutMs?: number;
  checkIntervalMs?: number;
  storageKey?: string;
  /** Injectable clock for testing. */
  now?: () => number;
}

export interface IdleLogoutController {
  start: () => void;
  stop: () => void;
  markActivity: () => void;
  check: () => void;
}

/**
 * Signs the user out after a period of inactivity. Activity across the tab
 * resets the countdown; the last-activity timestamp is mirrored to
 * sessionStorage so a page reload resumes the same window (and logs out
 * immediately if it already elapsed) rather than starting over.
 *
 * Arms itself automatically on login and disarms on logout. When a session is
 * already restored at mount, the host must call `start()` once (the auth state
 * doesn't transition, so the internal watcher won't fire for it).
 */
export function useIdleLogout(options: IdleLogoutOptions): IdleLogoutController {
  const auth = useAuthStore();
  const timeoutMs = options.timeoutMs ?? IDLE_TIMEOUT_MS;
  const checkIntervalMs = options.checkIntervalMs ?? IDLE_CHECK_INTERVAL_MS;
  const storageKey = options.storageKey ?? IDLE_STORAGE_KEY;
  const now = options.now ?? (() => Date.now());

  let lastActivity = now();
  let lastPersist = 0;
  let intervalId: ReturnType<typeof setInterval> | null = null;

  function readStored(): number | null {
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (raw === null) return null;
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    } catch {
      return null;
    }
  }

  function persist(ts: number): void {
    try {
      sessionStorage.setItem(storageKey, String(ts));
    } catch {
      // Private-mode / quota failures are non-fatal — the reload-resume just
      // won't work; the in-memory countdown still applies.
    }
  }

  function clearStored(): void {
    try {
      sessionStorage.removeItem(storageKey);
    } catch {
      // ignore
    }
  }

  function markActivity(): void {
    const t = now();
    lastActivity = t;
    // Throttle writes to at most ~once/sec — mousemove fires far too often.
    if (t - lastPersist >= 1000) {
      persist(t);
      lastPersist = t;
    }
  }

  function handleTimeout(): void {
    stop();
    clearStored();
    options.onTimeout();
  }

  function check(): void {
    if (!auth.isLoggedIn) return;
    if (now() - lastActivity >= timeoutMs) handleTimeout();
  }

  function onVisibility(): void {
    // A backgrounded tab throttles timers; re-check the moment it's foregrounded.
    if (document.visibilityState === 'visible') check();
  }

  function addListeners(): void {
    for (const ev of ACTIVITY_EVENTS) {
      window.addEventListener(ev, markActivity, { passive: true });
    }
    document.addEventListener('visibilitychange', onVisibility);
  }

  function removeListeners(): void {
    for (const ev of ACTIVITY_EVENTS) {
      window.removeEventListener(ev, markActivity);
    }
    document.removeEventListener('visibilitychange', onVisibility);
  }

  function start(): void {
    if (intervalId !== null) return;
    // Resume an in-progress countdown across reloads.
    const stored = readStored();
    lastActivity = stored ?? now();
    lastPersist = lastActivity;
    if (auth.isLoggedIn && now() - lastActivity >= timeoutMs) {
      handleTimeout();
      return;
    }
    persist(lastActivity);
    addListeners();
    intervalId = setInterval(check, checkIntervalMs);
  }

  function stop(): void {
    if (intervalId !== null) {
      clearInterval(intervalId);
      intervalId = null;
    }
    removeListeners();
  }

  watch(
    () => auth.isLoggedIn,
    (loggedIn) => {
      if (loggedIn) {
        markActivity();
        start();
      } else {
        stop();
        clearStored();
      }
    },
  );

  onBeforeUnmount(stop);

  return { start, stop, markActivity, check };
}
